import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import fetchWrapper from '../utils/httpClient';
import {
  API_DIGITAL_PRICE_FETCH_LIST,
  API_DIGITAL_PRICE_FETCH_LIST_SELECT_SHOP,
  API_UPLOAD_EXCEL,
  API_UPDATE_DIGITAL_PRICE_TAG_DATA,
  API_GET_ALL_SHOP_LIST
} from '../utils/apis';

//fetch list
export const getListAllDistData = createAsyncThunk(
  'digitalPrice/getListAllDistData',
  async (params) => {
    try {
      let url = `${API_DIGITAL_PRICE_FETCH_LIST}?page=${params.page}&size=${params.size}`;

      if (params.excelRecordId) {
        url += `&excelRecordId=${params.excelRecordId}`;
      }
      if (params.discount_type) {
        url += `&discountType=${params.discount_type}`;
      }
      if (params.sku) {
        url += `&sku=${params.sku}`;
      }
      if (params.promoDate) {
        url += `&promoDate=${params.promoDate}`;
      }
      if (params.skipIsActive !== true) {
        url += `&isActive=1`;
      }

      const response = await fetchWrapper.get(url);
      return {
        content: response.resdata.content,
        totalElements: response.resdata.totalElements,
        pageNumber: response.pageNumber,
        totalPages: response.resdata.totalPages
      };
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

// Add a POST request (specifically for the Selected Shop page)
export const getListSelectShopData = createAsyncThunk(
  'digitalPrice/getListSelectShopData',
  async (params) => {
    try {
      const payload = {
        page: params.page,
        size: params.size,
        excelRecordId: params.excelRecordId,
        discountType: 3,
        isActive: params.skipIsActive !== true ? 1 : undefined,
        shops: [...(params.selectedSingtel || []), ...(params.selectedShop || [])],
        sku: params.sku || '',
        promoDate: params.promoDate || ''
      };

      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      const response = await fetchWrapper.post(API_DIGITAL_PRICE_FETCH_LIST_SELECT_SHOP, payload);
      return {
        content: response.resdata.content,
        totalElements: response.resdata.totalElements,
        pageNumber: response.pageNumber,
        totalPages: response.resdata.totalPages
      };
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

export const fetchAllShopList = createAsyncThunk(
  'digitalPrice/fetchAllShopList',
  async () => {
    try {
      const response = await fetchWrapper.get(API_GET_ALL_SHOP_LIST);
      return response.resdata;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

// Asynchronous thunk for updating user information
export const uploadExcel = createAsyncThunk('digitalPrice/uploadExcel', async ({ file, discountType, disc_excel_shops }, { rejectWithValue }) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('discount_type', discountType);

    if (disc_excel_shops && disc_excel_shops.length > 0) {
      formData.append('disc_excel_shops', JSON.stringify(disc_excel_shops));
    }

    const response = await fetchWrapper.postFile(API_UPLOAD_EXCEL, formData);
    return response;
  } catch (error) {
    return rejectWithValue(error);
  }
});

// Asynchronous thunk for updating digital price tag data
export const updateDigitalPriceTagData = createAsyncThunk('digitalPrice/updateDigitalPriceTagData', async (payload) => {
  try {
    const response = await fetchWrapper.post(API_UPDATE_DIGITAL_PRICE_TAG_DATA, payload);
    return response;
  } catch (error) {
    throw new Error(error.message);
  }
});

const digitalPriceSlice = createSlice({
  name: 'digitalPrice',
  initialState: {
    resdata: [],
    totalElements: 0,
    pageNumber: 0,
    totalPages: 0,

    selectShopData: {
      resdata: [],
      totalElements: 0,
      pageNumber: 0,
      totalPages: 0,
      loading: false,
      error: null,
      status: "idle"
    },

    shopList: [],
    shopListLoading: false,
    shopListError: null,
    loading: false,
    error: null,
    status: "idle"
  },
  extraReducers: (builder) => {
    // Handle different states of getListAllDistData
    builder.addCase(getListAllDistData.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.status = "loading";
    });
    builder.addCase(getListAllDistData.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "succeeded";
      state.resdata = action.payload.content;
      state.totalElements = action.payload.totalElements;
      state.pageNumber = action.payload.pageNumber;
      state.totalPages = action.payload.totalPages;
    });
    builder.addCase(getListAllDistData.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
      state.status = "failed";
    });

    // Add getListSelectShopData processing
    builder.addCase(getListSelectShopData.pending, (state) => {
      state.selectShopData.loading = true;
      state.selectShopData.error = null;
      state.selectShopData.status = "loading";
    });
    builder.addCase(getListSelectShopData.fulfilled, (state, action) => {
      state.selectShopData.loading = false;
      state.selectShopData.status = "succeeded";
      state.selectShopData.resdata = action.payload.content;
      state.selectShopData.totalElements = action.payload.totalElements;
      state.selectShopData.pageNumber = action.payload.pageNumber;
      state.selectShopData.totalPages = action.payload.totalPages;
    });
    builder.addCase(getListSelectShopData.rejected, (state, action) => {
      state.selectShopData.loading = false;
      state.selectShopData.error = action.error.message;
      state.selectShopData.status = "failed";
    });

    // Handle different states of fetchAllShopList
    builder.addCase(fetchAllShopList.pending, (state) => {
      state.shopListLoading = true;
      state.shopListError = null;
    });
    builder.addCase(fetchAllShopList.fulfilled, (state, action) => {
      state.shopListLoading = false;
      state.shopList = action.payload;
    });
    builder.addCase(fetchAllShopList.rejected, (state, action) => {
      state.shopListLoading = false;
      state.shopListError = action.error.message;
    });

    // Handle different states of uploadExcel
    builder.addCase(uploadExcel.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.status = "loading";
    });
    builder.addCase(uploadExcel.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "succeeded";
    });
    builder.addCase(uploadExcel.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload.message;
      state.status = "failed";
    });

    // Handle different states of updateDigitalPriceTagData
    builder.addCase(updateDigitalPriceTagData.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.status = "loading";
    });
    builder.addCase(updateDigitalPriceTagData.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "succeeded";
      // You may want to update the state based on the response if needed
    });
    builder.addCase(updateDigitalPriceTagData.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
      state.status = "failed";
    });
  }
});

export default digitalPriceSlice.reducer;    